export interface ComparisonResult {
  current: number;
  previous: number;
  change: number;
  changePercent: number;
  trend: 'up' | 'down' | 'stable';
  isPositive: boolean;
}

export type ComparisonPeriod = 'wow' | 'mom' | 'custom';

export const comparisonUtils = {
  compare(current: number, previous: number, higherIsBetter: boolean = true): ComparisonResult {
    const change = current - previous;
    const changePercent = previous > 0 ? ((change / previous) * 100) : 0;

    let trend: 'up' | 'down' | 'stable' = 'stable';
    const threshold = 1;

    if (changePercent > threshold) {
      trend = 'up';
    } else if (changePercent < -threshold) {
      trend = 'down';
    }

    const isPositive = higherIsBetter
      ? changePercent >= 0
      : changePercent <= 0;

    return {
      current,
      previous,
      change,
      changePercent: Math.round(changePercent * 10) / 10,
      trend,
      isPositive
    };
  },

  formatChange(comparison: ComparisonResult): string {
    const sign = comparison.change >= 0 ? '+' : '';
    return `${sign}${comparison.change}`;
  },

  formatChangePercent(comparison: ComparisonResult): string {
    const sign = comparison.changePercent >= 0 ? '+' : '';
    return `${sign}${comparison.changePercent.toFixed(1)}%`;
  },

  getTrendIcon(trend: 'up' | 'down' | 'stable'): string {
    switch (trend) {
      case 'up':
        return '↑';
      case 'down':
        return '↓';
      case 'stable':
        return '→';
    }
  },

  getTrendColor(isPositive: boolean): string {
    return isPositive ? 'text-green-600' : 'text-red-600';
  },

  getTrendBackgroundColor(isPositive: boolean): string {
    return isPositive ? 'bg-green-50' : 'bg-red-50';
  },

  calculateGrowthRate(current: number, previous: number): number {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  },

  calculateCompoundGrowthRate(values: number[], periods: number): number {
    if (values.length < 2 || periods === 0) return 0;

    const first = values[0];
    const last = values[values.length - 1];

    if (first === 0) return 0;

    return (Math.pow(last / first, 1 / periods) - 1) * 100;
  },

  getWeekOverWeekComparison(current: number, lastWeek: number): ComparisonResult {
    return this.compare(current, lastWeek, true);
  },

  getMonthOverMonthComparison(current: number, lastMonth: number): ComparisonResult {
    return this.compare(current, lastMonth, true);
  },

  formatPeriodLabel(period: ComparisonPeriod): string {
    switch (period) {
      case 'wow':
        return 'vs Last Week';
      case 'mom':
        return 'vs Last Month';
      case 'custom':
        return 'vs Previous Period';
    }
  },

  calculateMovingAverage(values: number[], window: number): number[] {
    const result: number[] = [];

    for (let i = 0; i < values.length; i++) {
      const start = Math.max(0, i - window + 1);
      const windowValues = values.slice(start, i + 1);
      const avg = windowValues.reduce((sum, val) => sum + val, 0) / windowValues.length;
      result.push(avg);
    }

    return result;
  },

  detectAnomaly(current: number, historicalValues: number[], threshold: number = 2): boolean {
    if (historicalValues.length < 2) return false;

    const mean = historicalValues.reduce((sum, val) => sum + val, 0) / historicalValues.length;
    const variance = historicalValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / historicalValues.length;
    const stdDev = Math.sqrt(variance);

    const zScore = Math.abs((current - mean) / stdDev);

    return zScore > threshold;
  },

  getHealthScore(metrics: { value: number; target: number; weight: number }[]): number {
    let totalScore = 0;
    let totalWeight = 0;

    metrics.forEach(metric => {
      const achievement = metric.target > 0 ? (metric.value / metric.target) : 0;
      const score = Math.min(achievement, 1) * 100;
      totalScore += score * metric.weight;
      totalWeight += metric.weight;
    });

    return totalWeight > 0 ? Math.round(totalScore / totalWeight) : 0;
  }
};
